import { STAGE_LABEL } from '~/components/stages';
import type { DecisionSource, JobStatus, StageTimestamps } from '~/lib/api';

/**
 * The status timeline on JobDetailScreen. `pending_review` (D19, Phase 16) sits between
 * enriching and the terminal write: the machine has decided and a reviewer is verifying
 * before release. The console shows the label and NOTHING else — no queue, no review
 * affordance in the field app (BRIEF §5.4, task 16.5.19).
 */
export const TIMELINE: readonly JobStatus[] = [
  'queued',
  'ingesting',
  'analysing',
  'enriching',
  'pending_review',
  'complete',
];

/**
 * The stages this job actually went through.
 *
 * A job that was never held skips the review stage entirely rather than showing it greyed:
 * a stage drawn and never reached reads as one that failed. Whether it was held is known
 * from the job being held right now, or from a verdict a person released.
 */
export function timelineFor(
  status: JobStatus,
  decisionSource?: DecisionSource,
): readonly JobStatus[] {
  const reviewed =
    status === 'pending_review' ||
    decisionSource === 'human_verified' ||
    decisionSource === 'human_amended';
  return reviewed ? TIMELINE : TIMELINE.filter((s) => s !== 'pending_review');
}

/**
 * Display word for a timeline stage.
 *
 * `STAGE_LABEL` is the whole table now. It used to name `pending_review` alone and print
 * the raw status for the rest, so this screen showed "queued", "ingesting", "enriching"
 * — the API's own words — while the waiting screen showed English for the same sequence.
 */
export function timelineLabel(status: JobStatus): string {
  return STAGE_LABEL[status] ?? status;
}

/**
 * Index of the current stage on the timeline, -1 for failed. `partial` is a terminal
 * outcome and lights the same final dot as `complete`.
 */
export function stageIndexFor(status: JobStatus, stages: readonly JobStatus[] = TIMELINE): number {
  if (status === 'failed') return -1;
  return stages.indexOf(status === 'partial' ? 'complete' : status);
}

/**
 * Which stamp belongs to which row of the timeline.
 *
 * `complete` takes `released_at`, because from outside the machine the terminal event IS
 * the verdict being released. `partial` is the same event and shares it.
 *
 * `queued` reads `queued_at`, NOT `created_at`: 0024 keeps them apart on purpose —
 * created_at is when the row appeared and queued_at is when the job entered the queue.
 * They coincide today, which is why reading the wrong one would still look right.
 */
const STAGE_STAMP: Partial<Record<JobStatus, keyof StageTimestamps>> = {
  queued: 'queued_at',
  ingesting: 'ingesting_at',
  analysing: 'analysing_at',
  enriching: 'enriching_at',
  pending_review: 'held_at',
  complete: 'released_at',
  partial: 'released_at',
};

/**
 * The clock to print against a stage, or null when there is none.
 *
 * Null is the common case and must stay visible as nothing at all. Every stamp is
 * nullable server side, a stage the job has not reached has none, and every job written
 * before 0024 has only `queued_at` — so a dash or a zero here would be a measurement
 * nobody took, on the screen whose whole job is to say what happened when.
 */
export function stageTime(status: JobStatus, stages?: StageTimestamps): string | null {
  const key = STAGE_STAMP[status];
  const iso = key ? stages?.[key] : undefined;
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? null
    : at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
